import React, { useState } from 'react';
import { Toolbar, Avatar, Menu, MenuItem, Box, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { ROUTE_PATHS } from '@routes/RoutePaths';
import {
  AddOrganizationButton,
  ExternalNavigationLink,
  HeaderBar,
  NavigationLink,
  UserMenuButton,
} from '@components/layouts/HeaderStyles';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  getTicketmasterTransferErrorMessage,
  runTicketmasterTransfer,
  TICKETMASTER_TRANSFER_MODE_FULL,
  TICKETMASTER_TRANSFER_MODE_TEST,
  type TicketmasterTransferRunMode,
} from '@services/api/runTicketmasterTransfer';
import UserManualPanel from '@components/layouts/UserManualPanel';
import { USER_MANUAL_BUTTON_LABEL } from '@constants/user-manual.constants';

const Header = () => {
  const navigate = useNavigate();
  /* states */
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [ticketmasterMenuAnchor, setTicketmasterMenuAnchor] =
    useState<null | HTMLElement>(null);
  const [ticketmasterTransferLoading, setTicketmasterTransferLoading] =
    useState(false);
  const [userManualOpen, setUserManualOpen] = useState(false);

  /* handlers */
  const handleUserMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeUserMenu = () => {
    setAnchorEl(null);
  };

  const closeTicketmasterMenu = () => {
    setTicketmasterMenuAnchor(null);
  };

  const handleRunTicketmasterTransfer = async (
    mode: TicketmasterTransferRunMode
  ) => {
    closeTicketmasterMenu();
    setTicketmasterTransferLoading(true);
    try {
      await runTicketmasterTransfer(mode);
      if (mode === TICKETMASTER_TRANSFER_MODE_TEST) {
        toast.success(
          'Ticketmaster transfer started (test — stops after the first production create).'
        );
      } else {
        toast.success('Ticketmaster transfer started (full run).');
      }
    } catch (error) {
      toast.error(getTicketmasterTransferErrorMessage(error));
    } finally {
      setTicketmasterTransferLoading(false);
    }
  };

  return (
    <HeaderBar sx={{ marginBottom: '1rem' }} position="static">
      <Toolbar>
        <Box
          component={'img'}
          src={'/logo192.png'}
          alt="Logo"
          height={46}
          sx={{ display: 'flex', alignSelf: 'self-start', cursor: 'pointer' }}
          onClick={() => navigate(ROUTE_PATHS.HOME)}
        />
        <Box
          sx={{
            display: 'flex',
            flexGrow: 1,
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'center',
            columnGap: 0.5,
            rowGap: 0.5,
            minWidth: 0,
          }}
        >
          <NavigationLink
            variant="outlined"
            size={'small'}
            to={ROUTE_PATHS.HOME}
          >
            Organizations
          </NavigationLink>
          <NavigationLink
            variant="outlined"
            size={'small'}
            to={ROUTE_PATHS.EVENT_PAGE_HANDLER}
          >
            Event page handler
          </NavigationLink>
          <ExternalNavigationLink
            variant="outlined"
            size={'small'}
            onClick={() =>
              window.open(
                'https://data-acquisition.artelize.com/queues',
                '_blank'
              )
            }
          >
            Queues page
          </ExternalNavigationLink>
          {/* Temporary commented out */}
          {/*<NavigationLink*/}
          {/*  variant="outlined"*/}
          {/*  size={'small'}*/}
          {/*  to={ROUTE_PATHS.CRAWL_QUEUE}*/}
          {/*>*/}
          {/*  Crawl Queue*/}
          {/*</NavigationLink>*/}
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            marginRight: 0,
            // Keep this group from collapsing under the flexGrow center column (narrow viewports).
            flexShrink: 0,
          }}
        >
          <NavigationLink
            variant="outlined"
            size="small"
            to={ROUTE_PATHS.DRAFT_PRODUCTIONS}
          >
            Draft productions
          </NavigationLink>
          <Button
            variant="outlined"
            size="small"
            endIcon={<ArrowDropDownIcon />}
            onClick={(event) => setTicketmasterMenuAnchor(event.currentTarget)}
            disabled={ticketmasterTransferLoading}
            sx={{ textTransform: 'none' }}
          >
            Ticketmaster
          </Button>
          <Menu
            anchorEl={ticketmasterMenuAnchor}
            open={Boolean(ticketmasterMenuAnchor)}
            onClose={closeTicketmasterMenu}
          >
            <MenuItem
              onClick={() =>
                void handleRunTicketmasterTransfer(
                  TICKETMASTER_TRANSFER_MODE_TEST
                )
              }
            >
              Test (stop after first create)
            </MenuItem>
            <MenuItem
              onClick={() =>
                void handleRunTicketmasterTransfer(
                  TICKETMASTER_TRANSFER_MODE_FULL
                )
              }
            >
              Full transfer
            </MenuItem>
          </Menu>
          <AddOrganizationButton
            variant="contained"
            size={'small'}
            onClick={() => {
              navigate(ROUTE_PATHS.CREATE_ORGANIZATION);
              window.location.reload();
            }}
          >
            Add Organization
          </AddOrganizationButton>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setUserManualOpen((current) => !current)}
            sx={{ textTransform: 'none' }}
          >
            {USER_MANUAL_BUTTON_LABEL}
          </Button>
        </Box>
        <UserMenuButton
          variant={'outlined'}
          size={'small'}
          onClick={handleUserMenu}
        >
          <Avatar alt="User" src="/path-to-user-image.jpg" />
          <MenuIcon />
        </UserMenuButton>
        <Menu
          variant={'selectedMenu'}
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={closeUserMenu}
        >
          <MenuItem onClick={closeUserMenu}>Profile</MenuItem>
          <MenuItem onClick={closeUserMenu}>Logout</MenuItem>
        </Menu>
      </Toolbar>
      <UserManualPanel
        open={userManualOpen}
        onClose={() => setUserManualOpen(false)}
      />
    </HeaderBar>
  );
};

export default Header;
